// Pumpernickel Tycoon — main entry. Imports content modules and provides
// game logic + UI render in a single script (Phase 1 module split — content
// extracted to src/content/, system + ui split coming in 1c/1d).
import { TREATS } from './content/treats.js';
import { EVENTS } from './content/events.js';
import { ACHIEVEMENTS } from './content/achievements.js';
import { GOLDEN_BUFFS } from './content/golden-buffs.js';
import { MARKET_THRESHOLD } from './content/commodities.js';
import { PHASES } from './content/phases.js';
import { SPECIES } from './content/species.js';
import { CROWN_SHOP_ITEMS } from './content/crown-shop.js';

import { SFX, setMuted, isMuted } from './systems/audio.js';
import { initPumpPhysics, bouncePump, fireAura, pumpClickImpulse } from './systems/physics.js';
import { state, defaultState, replaceState, save, load, invalidatePassiveRate, emptyGarden, SAVE_KEY, SAVE_KEY_V1 } from './systems/state.js';
import { notifyChange } from './systems/store.js';
import { passiveRate, hasCrown, achievementBonus, crownBonus, eventPaceFactor, pantheonMod, hasActiveBuff, globalMult, rate, priceOf, lastPriceOf, meetsRequirement, bestRateBuy, BURNT_CRUST_DRAIN_PCT, BURNT_CRUST_PAYOUT_MULT, crownsForLifetime } from './systems/rate.js';
import { fmt } from './systems/util.js';
import { currentPhase, phaseTapMult, phaseComboMult, PHASE_DURATION_MS } from './systems/phases.js';
import { toast } from './ui/toast.js';
import { showBanner } from './ui/banner.js';
import { renderStreak, setPumpEmoji, resetHeaderCache, mountHeader } from './ui/header.jsx';
import { buildTreatRows, flashTreatRow } from './ui/treats.jsx';
import { initBakery, refreshBakery, bakerContribution } from './ui/bakery.js';
import { mountAchievements } from './ui/achievements.jsx';
import { initPantheonUi, mountPantheonUi } from './ui/pantheon.jsx';
import { initShop, mountShop } from './ui/shop.jsx';
import { initAscend, mountAscend } from './ui/ascend.jsx';
import { renderMarket, updateMarketPrices } from './ui/market.js';
import { initGarden, renderGarden, closeSpeciesPicker } from './ui/garden.js';
import { mountHelp, openHelp, closeHelp } from './ui/help.jsx';

// Tunables that stay in main for now — phase 1c moves them to systems/.
const COMBO_RESET_MS = 800;
const COMBO_THRESHOLDS = [10, 25, 50];   // [combo-1, combo-2, combo-3] streak entry
const COMBO_MULTIPLIERS = [1, 2, 3, 5];  // 1 → tier1=2 → tier2=3 → tier3=5 (deliberate skip of 4 for dramatic top tier)
const EVENT_MIN_MS = 30000;
const EVENT_MAX_MS = 90000;
const FIRST_EVENT_DELAY = 60000;
const WINK_MIN_MS = 8000;
const WINK_MAX_MS = 15000;
const WINK_DURATION_MS = 400;
const HAPTIC_MS = 8;
const HAPTIC_CRIT_MS = 18;
const OFFLINE_CAP_SEC = 8 * 3600;
const SAVE_INTERVAL_MS = 5000;
const TICK_MS = 1000;
const CRUMB_GLYPHS = ['·', '•', '◦', '∙'];
const CRUMB_COUNTS = [4, 2, 1, 1];     // index = comboTierIndex (0..3)
const CRUMB_DIST_MIN = 40;
const CRUMB_DIST_RANGE = 40;
const CRUMB_DURATION_MIN = 600;
const CRUMB_DURATION_RANGE = 200;
const CRUMB_LIFETIME_MS = 850;
const CRIT_CHANCE = 0.05;          // 5% per tap
const CRIT_MULT = 10;              // ×10 on crit
const CRIT_CRUMB_BONUS = 6;        // extra crumbs on crit
const TAP_RATE_FACTOR = 0.005;     // base click value scales with 5ms of current rate
const GOLDEN_MIN_MS = 60000;
const GOLDEN_MAX_MS = 180000;
const GOLDEN_LIFETIME_MS = 9000;
const GOLDEN_BONUS_SECONDS = 180;        // 3 minutes of rate per golden
const GOLDEN_BONUS_FALLBACK = 100;       // floor for early game when rate=0
const GOLDEN_BUFF_DURATION_MIN = 12000;  // random buff also fires on tap
const GOLDEN_BUFF_DURATION_MAX = 30000;
const CRUMB_RIPEN_MS = 60 * 60 * 1000;  // ripe crumb every 1 hour real-time
const BURNT_CRUST_THRESHOLD = 1e9;        // unlocks at 1B lifetime
const BURNT_CRUST_MIN_MS = 5 * 60 * 1000; // 5min between attempts
const BURNT_CRUST_MAX_MS = 15 * 60 * 1000;
const BURNT_CRUST_MAX = 3;                // cap active crusts
const ASCEND_THRESHOLD = 1e9;       // 1B lifetime to unlock first ascension
const AUTO_BUY_INTERVAL_TICKS = 5;



let streakCount = 0;
let lastTapTime = 0;
let streakResetTimer = null;

// Combo helpers stay in main.js — they read the streakCount module-level var
// owned by the tap handler.
function comboTier() {
  for (let i = COMBO_THRESHOLDS.length - 1; i >= 0; i--) {
    if (streakCount >= COMBO_THRESHOLDS[i]) return COMBO_MULTIPLIERS[i + 1];
  }
  return COMBO_MULTIPLIERS[0];
}
function comboTierIndex() {
  for (let i = COMBO_THRESHOLDS.length - 1; i >= 0; i--) {
    if (streakCount >= COMBO_THRESHOLDS[i]) return i + 1;
  }
  return 0;
}
function tapAmount() {
  const bonus = TREATS.filter(t => t.tapBonus).reduce((s, t) => s + (t.tapBonus * (state.owned[t.id] || 0)), 0);
  const fromRate = passiveRate() * TAP_RATE_FACTOR;
  const combo = comboTier() * phaseComboMult() * (1 + pantheonMod('combo'));
  return (1 + bonus + fromRate) * combo * globalMult() * phaseTapMult() * (1 + pantheonMod('tap'));
}

function sellOne(t) {
  const owned = state.owned[t.id] || 0;
  if (owned <= 0) return;
  const refund = Math.floor(lastPriceOf(t) * 0.5);
  state.owned[t.id] = owned - 1;
  invalidatePassiveRate();
  state.count += refund;
  // Lifetime isn't bumped — refunds recover past spend, not new earning.
  toast(`sold 1 ${t.name} · +${fmt(refund)}`);
  save(); refreshBakery();
}
const pumpEl = document.getElementById('pump');
const pumpInnerEl_ = document.getElementById('pump-inner');
const auraEl_ = document.getElementById('pump-aura');
initPumpPhysics({ pump: pumpEl, inner: pumpInnerEl_, aura: auraEl_ });
const companionEl = document.getElementById('companion');
const menuBtnEl = document.getElementById('menu-btn');
const menuModalEl = document.getElementById('menu-modal');
const menuInfoEl = document.getElementById('menu-info');

const goldenEl = document.getElementById('golden');

function buy(t) {
  const price = priceOf(t);
  if (state.count < price) return;
  state.count -= price;
  state.owned[t.id] = (state.owned[t.id] || 0) + 1;
  invalidatePassiveRate();
  if (t.doubleBank) {
    // Final bank = 2× pre-purchase bank. state.count just had `price` subtracted,
    // so add (pre + price) to land at 2*pre.
    const pre = state.count + price;
    const delta = pre + price;
    state.count += delta;
    state.lifetime += delta;
    toast(`✨ bank doubled. +${fmt(delta)}`);
  }
  if (t.hatch) { state.hasEgg = true; toast('🐣 a small dragon hatched.'); }
  if (t.id === 'crowncake') { toast('👑 the kingdom kneels.'); setPumpEmoji(); }
  SFX.buy();
  flashTreatRow(t.id);
  save(); refreshBakery(); checkAchievements();
}

function checkAchievements() {
  let any = false;
  for (const a of ACHIEVEMENTS) {
    if (state.achievements[a.id]) continue;
    if (a.check(state)) {
      state.achievements[a.id] = Date.now();
      toast(`🏆 ${a.name}`);
      any = true;
    }
  }
  if (any) {
    save(); refreshMenu();
    menuBtnEl.classList.remove('popped'); void menuBtnEl.offsetWidth; menuBtnEl.classList.add('popped');
    SFX.ach();
  }
}

function fireEvent() {
  if (passiveRate() === 0) return;
  const ev = EVENTS[Math.floor(Math.random() * EVENTS.length)];
  const isDebuff = ev.kind === 'debuff' || (ev.kind === 'instant_pct' && ev.factor < 0);
  // Bake phase blocks all debuffs.
  if (isDebuff && currentPhase().blockDebuffs) {
    showBanner(`🔥 bake phase shielded: ${ev.copy.split('.')[0]}`, 'buff');
    return;
  }
  // Yeast Insurance consumes one to block this debuff entirely.
  if (isDebuff && (state.owned.insurance || 0) > 0) {
    state.owned.insurance -= 1;
    showBanner(`☂️ insurance blocked: ${ev.copy.split('.')[0]}`, 'buff');
    save();
    return;
  }
  // Buff streak: 3+ buffs in a row makes the next buff hit twice as hard.
  const streakHot = ev.kind === 'buff' && state.buffsConsecutive >= 3;
  if (ev.kind === 'instant') {
    // Greedy boosts positive instants (debit events stay raw).
    const amt = ev.amount > 0 ? Math.floor(ev.amount * (1 + pantheonMod('instantAmount'))) : ev.amount;
    state.count = Math.max(0, state.count + amt);
    if (amt > 0) state.lifetime += amt;
  } else if (ev.kind === 'instant_pct') {
    const delta = Math.floor(state.count * ev.factor);
    state.count = Math.max(0, state.count + delta);
  } else if (ev.kind === 'buff' || ev.kind === 'debuff') {
    let mult = streakHot ? ev.mult * 2 : ev.mult;
    // Cunning weakens buff strength (only positive buffs — debuffs unaffected).
    if (mult > 1) mult = Math.max(1, 1 + (mult - 1) * (1 + pantheonMod('buffStrength')));
    // Lazy shortens buff duration (only buffs).
    let dur = ev.duration;
    if (ev.kind === 'buff') dur = Math.max(1000, dur * (1 + pantheonMod('buffDuration')));
    state.activeEffects.push({ id: ev.id, expiresAt: Date.now() + dur, mult });
  }
  if (ev.kind === 'buff') state.buffsConsecutive++;
  else if (isDebuff) state.buffsConsecutive = 0;
  const copy = streakHot ? ev.copy + ' (streak ×2!)' : ev.copy;
  showBanner(copy, ev.kind);
  if (ev.kind === 'buff') SFX.buff();
  else if (ev.kind === 'debuff') SFX.debuff();
  save(); checkAchievements();
}
function scheduleNextEvent() {
  const ph = currentPhase();
  const phasePace = ph.buffPace || (ph.suspendEvents ? 999 : 1);
  const wait = (EVENT_MIN_MS + Math.random() * (EVENT_MAX_MS - EVENT_MIN_MS)) * eventPaceFactor() * phasePace;
  setTimeout(() => { if (!currentPhase().suspendEvents) fireEvent(); scheduleNextEvent(); }, wait);
}

// Golden Pumpernickel — random clickable bonus.
let goldenExpiresAt = 0;
function spawnGolden() {
  if (passiveRate() === 0) { scheduleNextGolden(); return; }
  goldenEl.style.left = (Math.random() * 70 + 15) + '%';
  goldenEl.style.top = (Math.random() * 50 + 20) + '%';
  goldenEl.classList.add('active');
  if (hasCrown('goldenChime')) SFX.chime();
  goldenExpiresAt = Date.now() + GOLDEN_LIFETIME_MS;
  const myExpiry = goldenExpiresAt;
  setTimeout(() => {
    if (goldenExpiresAt === myExpiry) {
      goldenEl.classList.remove('active');
      goldenExpiresAt = 0;
      scheduleNextGolden();
    }
  }, GOLDEN_LIFETIME_MS);
}
function scheduleNextGolden() {
  // Cunning temperament makes goldens fire faster.
  const freqMod = 1 + pantheonMod('goldenFreq');
  const wait = (GOLDEN_MIN_MS + Math.random() * (GOLDEN_MAX_MS - GOLDEN_MIN_MS)) / Math.max(0.1, freqMod);
  setTimeout(spawnGolden, wait);
}
goldenEl.addEventListener('pointerdown', (e) => {
  e.stopPropagation();
  if (!goldenEl.classList.contains('active')) return;
  // Use baseline (no active-buff) rate so a golden_storm/cosmic_align
  // doesn't compound into a 10-minute jackpot per the balance review.
  const baseRate = passiveRate() * (1 + achievementBonus() + crownBonus());
  const gain = Math.max(Math.floor(baseRate * GOLDEN_BONUS_SECONDS), GOLDEN_BONUS_FALLBACK);
  state.count += gain; state.lifetime += gain;
  state.goldensClicked = (state.goldensClicked || 0) + 1;
  // Roll a random buff to fire alongside the instant grant.
  const buff = GOLDEN_BUFFS[Math.floor(Math.random() * GOLDEN_BUFFS.length)];
  const dur = GOLDEN_BUFF_DURATION_MIN + Math.random() * (GOLDEN_BUFF_DURATION_MAX - GOLDEN_BUFF_DURATION_MIN);
  state.activeEffects.push({ id: buff.id, expiresAt: Date.now() + dur, mult: buff.mult });
  state.buffsConsecutive = (state.buffsConsecutive || 0) + 1;
  SFX.golden();
  toast(`✨ +${fmt(gain)} · ${buff.copy} (${Math.round(dur/1000)}s)`);
  // Burst of crit-styled crumb particles at the click point.
  const r = goldenEl.getBoundingClientRect();
  const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
  for (let i = 0; i < 12; i++) {
    const c = document.createElement('div');
    c.className = 'crumb crit';
    c.textContent = CRUMB_GLYPHS[Math.floor(Math.random() * CRUMB_GLYPHS.length)];
    c.style.position = 'fixed';
    c.style.left = cx + 'px'; c.style.top = cy + 'px';
    const angle = Math.random() * Math.PI * 2;
    const dist = 60 + Math.random() * 60;
    c.style.setProperty('--cx', Math.cos(angle) * dist + 'px');
    c.style.setProperty('--cy', Math.sin(angle) * dist + 'px');
    c.style.setProperty('--cr', (Math.random() * 360 - 180) + 'deg');
    c.style.animation = `crumb-fly ${CRUMB_DURATION_MIN + Math.random() * CRUMB_DURATION_RANGE}ms forwards ease-out`;
    document.body.appendChild(c);
    setTimeout(() => c.remove(), CRUMB_LIFETIME_MS);
  }
  goldenEl.classList.remove('active');
  goldenExpiresAt = 0;
  save();
  scheduleNextGolden();
});

// Burnt Crusts — late-game pests that drain a baker, pop for 110% payout.
function attachBurntCrust() {
  if (!state.burntCrusts) state.burntCrusts = [];
  if (state.burntCrusts.length >= BURNT_CRUST_MAX) { scheduleNextBurntCrust(); return; }
  if (state.lifetime < BURNT_CRUST_THRESHOLD) { scheduleNextBurntCrust(); return; }
  // Pick a random owned production baker.
  const owned = TREATS.filter(t => t.rate && (state.owned[t.id] || 0) > 0);
  if (owned.length === 0) { scheduleNextBurntCrust(); return; }
  const t = owned[Math.floor(Math.random() * owned.length)];
  state.burntCrusts.push({ bakerId: t.id, attachedAt: Date.now(), drained: 0 });
  invalidatePassiveRate();
  showBanner(`🥵 a burnt crust attached to your ${t.name}. tap to pop.`, 'debuff');
  refreshBakery();
  scheduleNextBurntCrust();
}
function scheduleNextBurntCrust() {
  const wait = BURNT_CRUST_MIN_MS + Math.random() * (BURNT_CRUST_MAX_MS - BURNT_CRUST_MIN_MS);
  setTimeout(attachBurntCrust, wait);
}
function popBurntCrust(crustIdx) {
  if (!state.burntCrusts || crustIdx < 0 || crustIdx >= state.burntCrusts.length) return;
  const c = state.burntCrusts[crustIdx];
  const payout = Math.floor(c.drained * BURNT_CRUST_PAYOUT_MULT);
  state.count += payout;
  state.lifetime += Math.floor(c.drained * (BURNT_CRUST_PAYOUT_MULT - 1)); // only the 10% bonus, not the recovery
  state.burntCrusts.splice(crustIdx, 1);
  invalidatePassiveRate();
  toast(`🥵 popped! +${fmt(payout)}`);
  SFX.golden();
  save(); refreshBakery();
}

// Companion wink
function scheduleWink() {
  const wait = WINK_MIN_MS + Math.random() * (WINK_MAX_MS - WINK_MIN_MS);
  setTimeout(() => {
    if (state.hasEgg) {
      companionEl.textContent = '😉';
      setTimeout(() => { companionEl.textContent = '🐣'; }, WINK_DURATION_MS);
    }
    scheduleWink();
  }, wait);
}

// Tap handler
pumpEl.addEventListener('pointerdown', (e) => {
  const now = Date.now();
  const prevTier = comboTierIndex();
  // Knead phase doubles the combo decay window, letting streaks last longer.
  const decayMs = COMBO_RESET_MS * (currentPhase().streakDecayMult || 1);
  if (now - lastTapTime < decayMs) streakCount++;
  else streakCount = 1;
  lastTapTime = now;
  if (streakCount > state.longestStreak) state.longestStreak = streakCount;
  clearTimeout(streakResetTimer);
  streakResetTimer = setTimeout(() => { streakCount = 0; renderStreak(0, 0, 1); }, decayMs);
  // Tier-up aura burst when crossing a combo threshold.
  const newTier = comboTierIndex();
  if (newTier > prevTier && newTier > 0) { fireAura(); toast(`🔥 streak ×${comboTier()}`); }
  bouncePump();
  pumpClickImpulse();

  const baseCritChance = hasCrown('ironKnuckles') ? 0.10 : CRIT_CHANCE;
  const critChance = Math.max(0, baseCritChance * (1 + pantheonMod('crit')));
  const critMult = hasCrown('luckyHands') ? 15 : CRIT_MULT;
  const crit = Math.random() < critChance;
  const gain = Math.floor(tapAmount() * (crit ? critMult : 1));
  state.count += gain; state.lifetime += gain; state.totalTaps++;
  if (crit) state.criticalTaps = (state.criticalTaps || 0) + 1;
  const rect = pumpEl.getBoundingClientRect();
  const x = e.clientX - rect.left, y = e.clientY - rect.top;
  const f = document.createElement('div');
  f.className = 'float' + (crit ? ' crit' : ''); f.textContent = (crit ? '✦+' : '+') + fmt(gain);
  f.style.left = x + 'px'; f.style.top = y + 'px';
  pumpEl.appendChild(f);
  setTimeout(() => f.remove(), 900);
  // Crumb particles — fewer at high streak; extra burst on crit.
  const crumbCount = CRUMB_COUNTS[comboTierIndex()] + (crit ? CRIT_CRUMB_BONUS : 0);
  for (let i = 0; i < crumbCount; i++) {
    const c = document.createElement('div');
    c.className = 'crumb' + (crit ? ' crit' : '');
    c.textContent = CRUMB_GLYPHS[Math.floor(Math.random() * CRUMB_GLYPHS.length)];
    c.style.left = x + 'px'; c.style.top = y + 'px';
    const angle = Math.random() * Math.PI * 2;
    const dist = CRUMB_DIST_MIN + Math.random() * CRUMB_DIST_RANGE * (crit ? 1.6 : 1);
    c.style.setProperty('--cx', Math.cos(angle) * dist + 'px');
    c.style.setProperty('--cy', Math.sin(angle) * dist + 'px');
    c.style.setProperty('--cr', (Math.random() * 360 - 180) + 'deg');
    c.style.animation = `crumb-fly ${CRUMB_DURATION_MIN + Math.random() * CRUMB_DURATION_RANGE}ms ease-out forwards`;
    pumpEl.appendChild(c);
    setTimeout(() => c.remove(), CRUMB_LIFETIME_MS);
  }
  renderStreak(streakCount, comboTierIndex(), comboTier()); checkAchievements();
  notifyChange();
  if (navigator.vibrate) navigator.vibrate(crit ? HAPTIC_CRIT_MS : HAPTIC_MS);
  (crit ? SFX.crit : SFX.tap)();
});

// Single menu — tabs for ach / shop / ascend.
let activeTab = 'ach';
function switchTab(tab) {
  // Skip disabled tabs (shop unlocks at first ascension, ascend at 1B lifetime).
  const tabBtn = document.querySelector(`.menu-tab[data-tab="${tab}"]`);
  if (tabBtn && tabBtn.disabled) return;
  activeTab = tab;
  document.querySelectorAll('.menu-tab').forEach(b => b.classList.toggle('active', b.dataset.tab === tab));
  document.querySelectorAll('.menu-pane').forEach(p => p.hidden = (p.dataset.tab !== tab));
  // Most panels are preact + store-subscribed, no manual refresh needed.
  // garden/market are still imperative; refresh only when their tab opens.
  if (tab === 'garden') renderGarden();
  else if (tab === 'market') renderMarket();
}
function refreshMenu() {
  // Tab availability gates content rather than hiding tabs.
  const shopTab = document.querySelector('.menu-tab[data-tab="shop"]');
  const ascendTab = document.querySelector('.menu-tab[data-tab="ascend"]');
  const gardenTab = document.querySelector('.menu-tab[data-tab="garden"]');
  shopTab.disabled = (state.ascensions || 0) < 1 && (state.crowns || 0) === 0;
  ascendTab.disabled = state.lifetime < ASCEND_THRESHOLD && (state.crowns || 0) === 0;
  if (gardenTab) gardenTab.disabled = (state.crumbs || 0) === 0 && state.garden && state.garden.every(p => !p.species);
  const pantheonTab = document.querySelector('.menu-tab[data-tab="pantheon"]');
  if (pantheonTab) pantheonTab.disabled = (state.ascensions || 0) < 1;
  const marketTab = document.querySelector('.menu-tab[data-tab="market"]');
  if (marketTab) marketTab.disabled = (state.lifetimeBaked || 0) + state.lifetime < MARKET_THRESHOLD;
  // If active tab is now disabled, fall back to ach.
  const activeBtn = document.querySelector(`.menu-tab[data-tab="${activeTab}"]`);
  if (activeBtn && activeBtn.disabled) { activeTab = 'ach'; }
  // Compact info next to ☰ — earned/total achievements + crown count when relevant.
  const earned = Object.keys(state.achievements || {}).length;
  const crowns = state.crowns || 0;
  menuInfoEl.textContent = crowns > 0 ? `${earned}/${ACHIEVEMENTS.length} · ${crowns}👑` : `${earned}/${ACHIEVEMENTS.length}`;
  // Pulse the menu button when an ascension is earnable.
  const earnable = state.lifetime >= ASCEND_THRESHOLD ? crownsForLifetime(state.lifetime) - crowns : 0;
  menuBtnEl.classList.toggle('pulse', earnable > 0);
}
menuBtnEl.addEventListener('click', () => {
  refreshMenu();
  switchTab(activeTab);
  menuModalEl.classList.add('show');
});
document.getElementById('menu-close').addEventListener('click', () => menuModalEl.classList.remove('show'));
menuModalEl.addEventListener('click', (e) => { if (e.target === menuModalEl) menuModalEl.classList.remove('show'); });
document.querySelectorAll('.menu-tab').forEach(btn => btn.addEventListener('click', () => switchTab(btn.dataset.tab)));

// Prestige — ascend the tinyclaw. Display lives in ui/ascend.jsx; this is
// the mutation. crownsForLifetime is in systems/rate.js as the single source
// of truth (imported by both this module and ui/ascend.jsx).
function doAscend() {
  const earnable = crownsForLifetime(state.lifetime);
  const gain = earnable - (state.crowns || 0);
  if (gain <= 0) { menuModalEl.classList.remove('show'); return; }
  // Carry forward: economy resets, but progression markers persist so unlocked
  // achievements stay coherent (no vanishing egg, no re-armed 24h timer, etc).
  const shop = state.crownShop || {};
  const carry = {
    crowns: (state.crowns || 0) + gain,
    ascensions: (state.ascensions || 0) + 1,
    lifetimeBaked: (state.lifetimeBaked || 0) + state.lifetime,
    achievements: state.achievements || {},
    crownShop: shop,
    totalTaps: state.totalTaps || 0,
    longestStreak: state.longestStreak || 0,
    criticalTaps: state.criticalTaps || 0,
    goldensClicked: state.goldensClicked || 0,
    hasEgg: !!state.hasEgg || !!shop.eternalEgg,
    startedAt: state.startedAt || Date.now(),
    crumbs: state.crumbs || 0,
    lastCrumbAt: state.lastCrumbAt || Date.now(),
    phaseIndex: state.phaseIndex || 0,
    phaseStartAt: state.phaseStartAt || Date.now(),
    garden: state.garden || emptyGarden(),
    barleyBonus: state.barleyBonus || 0,
    pantheon: state.pantheon || [null, null, null],
    market: state.market || { holdings: {}, prices: {}, lastUpdateAt: 0 },
  };
  // Auto-pop all attached burnt crusts at ascend time so accumulated drain
  // isn't silently lost when bakers reset. Pays out at the standard 110%.
  let crustPayout = 0;
  for (const c of (state.burntCrusts || [])) crustPayout += Math.floor(c.drained * BURNT_CRUST_PAYOUT_MULT);
  replaceState(Object.assign(defaultState(), carry));
  if (shop.reinforced) state.owned.apprentice = (state.owned.apprentice || 0) + 1;
  invalidatePassiveRate();
  streakCount = 0;
  resetHeaderCache();
  setPumpEmoji();
  if (crustPayout > 0) state.count += crustPayout;
  menuModalEl.classList.remove('show');
  const flash = document.getElementById('ascend-flash');
  flash.classList.remove('show'); void flash.offsetWidth; flash.classList.add('show');
  SFX.ascend();
  const tail = crustPayout > 0 ? ` (carried crust payout: +${fmt(crustPayout)})` : '';
  toast(`✨ ascended. +${gain}👑 — total ${state.crowns}${tail}`);
  save(); refreshBakery(); refreshMenu();
}

// Crown Shop — spend crowns on permanent meta-upgrades.
function buyShopItem(id) {
  const item = CROWN_SHOP_ITEMS.find(x => x.id === id);
  if (!item) return;
  if (state.crownShop && state.crownShop[id]) return;
  if ((state.crowns || 0) < item.cost) return;
  state.crowns -= item.cost;
  state.crownShop = state.crownShop || {};
  state.crownShop[id] = true;
  if (id === 'eternalEgg') state.hasEgg = true;
  if (id === 'reinforced') {
    state.owned.apprentice = (state.owned.apprentice || 0) + 1;
    invalidatePassiveRate();
    refreshBakery();
  }
  toast(`🪙 ${item.name}`);
  save();
  refreshMenu();
}

// Tinyclaw Pantheon — slot 3 temperaments with weighted effects.

// Help modal is a Preact component (phase 3). main.js wires the trigger
// button + Escape key; the modal owns its own open/close state internally.
mountHelp(document.getElementById('help-modal-mount'));
document.getElementById('help-btn').addEventListener('click', openHelp);

// Escape key dismisses any open modal — desktop a11y expectation.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  if (menuModalEl.classList.contains('show')) { menuModalEl.classList.remove('show'); return; }
  closeHelp();
  // Species picker is created on demand; close any open one.
  closeSpeciesPicker();
});

// Suppress the iOS/Android long-press context menu on bakery chips so the
// 500ms sell-back hold doesn't race with native image-save / text-select UI.
document.addEventListener('contextmenu', (e) => {
  if (e.target.closest('.asset')) e.preventDefault();
});

document.getElementById('mute-btn').addEventListener('click', () => setMuted(!isMuted()));
setMuted(isMuted());

document.getElementById('reset').addEventListener('click', () => {
  if (!confirm('Reset all progress?')) return;
  localStorage.removeItem(SAVE_KEY);
  localStorage.removeItem(SAVE_KEY_V1);
  replaceState(defaultState());
  streakCount = 0;
  resetHeaderCache();
  setPumpEmoji();
  refreshBakery();
  refreshMenu();
  notifyChange(); // fire preact subscribers immediately, otherwise UI is 1s stale
});

// Tick — passive accumulation + treat-affordability refresh + achievement check.
let tickCount = 0;
setInterval(() => {
  tickCount++;
  const now = Date.now();
  // Real-elapsed-since-last-tick — used by garden so background-tab throttling
  // doesn't pause plant growth in wall-clock time. Cap at 5min to avoid
  // single-tick offline jumps that mature everything at once.
  const dt = Math.min(now - (state.lastTick || now), 5 * 60 * 1000);
  state.activeEffects = state.activeEffects.filter(e => now < e.expiresAt);
  const r = rate();
  if (r > 0) { state.count += r; state.lifetime += r; }
  state.lastTick = now;
  if (hasCrown('steward') && tickCount % AUTO_BUY_INTERVAL_TICKS === 0) {
    const t = bestRateBuy();
    if (t && state.count >= priceOf(t)) buy(t);
  }
  // Crumb Trail — a ripe crumb appears every CRUMB_RIPEN_MS (catches up if offline).
  while (now - state.lastCrumbAt >= CRUMB_RIPEN_MS) {
    state.crumbs = (state.crumbs || 0) + 1;
    state.lastCrumbAt += CRUMB_RIPEN_MS;
    toast(`🍞 a ripe crumb appeared. you have ${state.crumbs}.`);
  }
  // Yeast Market — tick price refresh in the background even if modal is closed.
  if (state.market) updateMarketPrices();
  // Burnt Crusts — accumulate drained value per-second for the pop payout.
  if (state.burntCrusts && state.burntCrusts.length > 0) {
    for (const c of state.burntCrusts) {
      const t = TREATS.find(x => x.id === c.bakerId);
      if (!t) continue;
      // Per-tick drain = per-baker contribution × drain pct, scaled by globalMult
      // so payout matches "real" lost rate (ach/crown bonuses apply too).
      c.drained += bakerContribution(t).contribution * BURNT_CRUST_DRAIN_PCT * globalMult();
    }
    refreshBakery();
  }
  // Yeast Garden — advance plot grow timers by real elapsed time so a
  // backgrounded tab still sees plants mature at the advertised rate.
  // Stardust pauses when no buff is up.
  if (state.garden.some(p => p.species)) {
    const buffActive = hasActiveBuff();
    for (let i = 0; i < state.garden.length; i++) {
      const plot = state.garden[i];
      if (!plot.species) continue;
      const sp = SPECIES.find(s => s.id === plot.species);
      if (!sp) continue;
      if (sp.requiresBuff && !buffActive) continue;
      plot.lastTickAt = Math.min(plot.plantedAt + sp.growMs, plot.lastTickAt + dt);
    }
  }
  // Sourdough Phases — global rule cycler. Advances on real elapsed time.
  while (now - state.phaseStartAt >= PHASE_DURATION_MS) {
    state.phaseStartAt += PHASE_DURATION_MS;
    state.phaseIndex = ((state.phaseIndex || 0) + 1) % PHASES.length;
    const ph = currentPhase();
    showBanner(`${ph.icon} entering ${ph.name} phase — ${ph.desc}`, 'buff');
  }
  // Cool phase: bank +10% every 60s (no events firing during this phase).
  if (currentPhase().bankBonusEvery && tickCount % currentPhase().bankBonusEvery === 0 && state.count > 0) {
    const bonus = Math.floor(state.count * currentPhase().bankBonusPct);
    if (bonus > 0) { state.count += bonus; state.lifetime += bonus; }
  }
  // header pane (count/rate/phase/effects) + treats + shop + ach are all
  // preact + store-subscribed; notifyChange below fires their re-renders.
  refreshMenu();
  checkAchievements();
  // If a tab with live timers is open, refresh it so countdowns don't freeze.
  if (menuModalEl.classList.contains('show')) {
    if (activeTab === 'garden') renderGarden();
    else if (activeTab === 'market') renderMarket();
    // ascend pane (preact) auto-refreshes via store subscription
  }
  // Notify store subscribers (Phase 2). Vanilla render calls above are still
  // explicit; once Phase 3 lands, panels subscribe and the explicit calls go
  // away. Tap handler also notifies — see pump pointerdown below.
  notifyChange();
}, TICK_MS);
setInterval(save, SAVE_INTERVAL_MS);
window.addEventListener('beforeunload', save);

if (load()) {
  // Offline gain — applied here because rate() depends on systems we don't
  // import inside state.js. Cap by Lazy-pantheon-bumped offline cap.
  const offlineCap = OFFLINE_CAP_SEC * (1 + pantheonMod('offline'));
  const dt = Math.min((Date.now() - state.lastTick) / 1000, offlineCap);
  invalidatePassiveRate();
  const offlineGain = Math.floor(dt * rate());
  if (offlineGain > 0) { state.count += offlineGain; state.lifetime += offlineGain; toast(`+${fmt(offlineGain)} while you were gone`); }
  state.lastTick = Date.now();
}
invalidatePassiveRate();
initBakery({ onSellCb: sellOne, onPopCrustCb: popBurntCrust });
mountAchievements();
initPantheonUi({ onRefreshMenuCb: refreshMenu });
mountPantheonUi();
initShop({ onBuyShopItemCb: buyShopItem });
mountShop();
initAscend({ onAscendCb: doAscend });
mountAscend();
initGarden({ onRefreshMenuCb: refreshMenu, onSpawnGoldenCb: spawnGolden });
buildTreatRows(buy);
refreshBakery();
mountHeader();
refreshMenu();
setTimeout(() => scheduleNextEvent(), FIRST_EVENT_DELAY);
scheduleNextBurntCrust();
scheduleNextGolden();
scheduleWink();
checkAchievements();
